import { FC } from 'react';

import { RadioCardGroupProps, RadioCardItem } from '@purpur/library';

import { MessagesType } from '@telialt/common-cms/src/model';
import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { ESHOP_TEST_ID } from '@telialt/core-eshop/src/test/test-ids';

import { GiftOption } from '../model';
import { CopyLocalCode } from './CopyLocalCode';
import {
    GiftPrice,
    GiftPriceOld,
    ImageWithOffset,
    OpenGiftDetailsButtonWithOffset,
    RadioGiftCardGroup,
} from './GiftGroup.styles';

const toRadioCardItem = ({ code, image, name, price, oldPriceCrossed, onOpenDetails }: GiftOption, t: MessagesType) => {
    const body = (
        <div>
            <GiftPrice
                data-test={ESHOP_TEST_ID.GIFT_ITEM_PRICE_BLOCK}
                aria-label={`${t['eshop.product_page.price_accessible']} ${price}`}
            >
                <span aria-hidden={true}>{price}</span>
            </GiftPrice>
            {oldPriceCrossed && (
                <GiftPriceOld
                    data-test={ESHOP_TEST_ID.GIFT_ITEM_OLD_PRICE_BLOCK}
                    aria-label={`${t['eshop.product_page.old_price_accessible']} ${oldPriceCrossed}`}
                >
                    <span aria-hidden={true}>{oldPriceCrossed}</span>
                </GiftPriceOld>
            )}
            <CopyLocalCode>{code}</CopyLocalCode>
        </div>
    );

    return (
        <RadioCardItem
            key={code}
            id={code}
            title={name}
            value={code}
            body={body}
            image={<ImageWithOffset {...image} />}
        >
            <OpenGiftDetailsButtonWithOffset onClick={onOpenDetails} />
        </RadioCardItem>
    );
};

type RadioGiftGroupProps = Pick<RadioCardGroupProps, 'defaultValue' | 'onValueChange'> & {
    options: GiftOption[];
    labelledBy?: string;
};

const ID = 'gift-selector';

export const RadioGiftGroup: FC<RadioGiftGroupProps> = ({ defaultValue, onValueChange, options, labelledBy }) => {
    const { t } = useTranslations();

    return (
        <RadioGiftCardGroup
            defaultValue={defaultValue}
            id={ID}
            name={ID}
            onValueChange={onValueChange}
            radioPosition="left"
            data-test={ESHOP_TEST_ID.GIFT_ITEM_RADIO}
            aria-labelledby={labelledBy}
        >
            {options.map((option) => toRadioCardItem(option, t))}
        </RadioGiftCardGroup>
    );
};
